import { useEffect, useRef, useState, useMemo, createContext } from "react";
import * as Tone from "tone";
import { motion } from "framer-motion";
import "./AdaptiveMusicPlayer.css";
import FlowVisualization from "./FlowVisualization";
import { musicSystem } from "../databases/adaptiveMusicConfig";

// Music state context for visualizations
export const MusicStateContext = createContext(null);

// Game states for adaptive music
const GAME_STATES = ["idle", "exploration", "tension", "combat", "victory"];

const STATE_DESCRIPTIONS = {
  idle: {
    title: "Ambient State",
    description: "Base layer with subtle atmospheric textures",
  },
  exploration: {
    title: "Exploration Mode",
    description: "Layered melody introduces with rhythmic elements",
  },
  tension: {
    title: "Rising Tension",
    description: "Harmonic tension builds with intensifying percussion",
  },
  combat: {
    title: "Combat Intensity",
    description: "Full orchestration with maximum dynamic range",
  },
  victory: {
    title: "Resolution",
    description: "Triumphant motifs with gradual de-escalation",
  },
};

// Initial layers configuration
const INITIAL_LAYERS = [
  { name: "Ambient Pad", active: true, volume: 0.8 },
  { name: "Melodic Layer", active: false, volume: 0 },
  { name: "Rhythmic Base", active: false, volume: 0 },
  { name: "Percussion", active: false, volume: 0 },
  { name: "Orchestral", active: false, volume: 0 },
];

// State to layer configuration mapping
const STATE_LAYER_CONFIG = {
  idle: { active: [0], intensity: 1 },
  exploration: { active: [0, 1], intensity: 2 },
  tension: { active: [0, 1, 2], intensity: 3 },
  combat: { active: [0, 1, 2, 3, 4], intensity: 5 },
  victory: { active: [0, 1, 4], intensity: 4 },
};

const AdaptiveMusicPlayer = ({
  visualization: VisualizationComponent,
  showDevView = true,
}) => {
  // Player refs - dynamically managed based on config
  const playersRef = useRef({}); // Map of trackId -> Tone.Player
  const transitionPlayersRef = useRef({}); // Map of transitionId -> Tone.Player

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(
    musicSystem.initialPosition,
  );
  const [activeTracks, setActiveTracks] = useState([]); // Array of currently playing trackObject IDs
  const [liveTimelinePos, setLiveTimelinePos] = useState(0); // Live playback position
  const [timelinePositions, setTimelinePositions] = useState({}); // Map of timelineId -> current position
  const [parameterA, setParameterA] = useState(0.5); // Volume parameter (0-1)
  const [parameterB, setParameterB] = useState(1.0); // Filter parameter (0-1, 0=closed, 1=open)
  const [transitionParam, setTransitionParam] = useState(0); // Transition parameter (0=none, 1+=transition targets)
  const [activeTransition, setActiveTransition] = useState(null); // { id, startTime, duration }
  const [transitionProgress, setTransitionProgress] = useState(0); // 0-1 progress through transition

  // New state for game states, intensity, and layers
  const [gameState, setGameState] = useState("idle");
  const [intensity, setIntensity] = useState(1);
  const [layers, setLayers] = useState(INITIAL_LAYERS);

  // Update layers when game state changes
  useEffect(() => {
    const config = STATE_LAYER_CONFIG[gameState];
    setIntensity(config.intensity);
    setLayers((prev) =>
      prev.map((layer, i) => ({
        ...layer,
        active: config.active.includes(i),
        volume: config.active.includes(i) ? 0.6 + Math.random() * 0.4 : 0,
      })),
    );
  }, [gameState]);
  const scheduledEventRef = useRef(null);
  const timelineStartRef = useRef(0); // When timeline playback started
  const absoluteTimelineStartRef = useRef(0); // Absolute playback start (never resets during loops)
  const animationFrameRef = useRef(null); // For position updates
  const volumeNodesRef = useRef({}); // Store Tone.Volume nodes for each track
  const envelopeNodesRef = useRef({}); // Store envelope gain nodes for AR control
  const filterNodesRef = useRef({}); // Store filter nodes for each track

  // Helper function to recursively resolve trackObjects into playable tracks
  const resolveTrackObjects = (
    timelineId,
    timelineStartTime = 0,
    parentEndTime = Infinity,
    parentEnvelope = null,
  ) => {
    const timeline = musicSystem.timelines[timelineId];
    if (!timeline) return [];

    const resolvedTracks = [];

    timeline.trackObjects.forEach((objId) => {
      const trackObj = musicSystem.trackObjects[objId];
      if (!trackObj) return;

      if (trackObj.type === "track") {
        // Leaf node: wrap the track
        const track = musicSystem.tracks[trackObj.trackRef];
        if (track) {
          // Track plays until its own endTime (respects parentEndTime if nested)
          const effectiveEndTime = Math.min(
            timelineStartTime + trackObj.endTime,
            parentEndTime,
          );

          resolvedTracks.push({
            trackId: track.id,
            trackObjId: trackObj.id,
            timelineId: timelineId,
            url: track.url,
            playbackOffset: track.playbackOffset,
            startTime: timelineStartTime + trackObj.startTime,
            endTime: effectiveEndTime,
            envelope: parentEnvelope, // Inherit envelope from parent trackObject
            // Store absolute loop regions for main timeline
            absoluteLoopRegions: timeline.loopRegions
              ? timeline.loopRegions.map((region) => ({
                  start: timelineStartTime + region.start,
                  end: timelineStartTime + region.end,
                }))
              : [],
            // Store the timeline offset (for nested timelines)
            timelineOffset: timelineStartTime,
            // Store the parent container's end time (for nested timelines that loop)
            nestedContainerEndTime: parentEndTime,
            // Store track duration within the timeline
            trackDuration: trackObj.endTime - trackObj.startTime,
            volumeParams: trackObj.volumeParams || {},
            filterParams: trackObj.filterParams || {},
          });
        }
      } else if (trackObj.type === "timeline") {
        // Recursive: resolve nested timeline
        const nestedStartTime = timelineStartTime + trackObj.startTime;
        const nestedEndTime = timelineStartTime + trackObj.endTime;
        const envelope = trackObj.envelope || null;
        const nestedTracks = resolveTrackObjects(
          trackObj.timelineRef,
          nestedStartTime,
          nestedEndTime,
          envelope,
        );
        resolvedTracks.push(...nestedTracks);
      }
    });

    return resolvedTracks;
  };

  // Get all resolved tracks from root timeline
  const getResolvedTracks = () => {
    return resolveTrackObjects(musicSystem.rootTimeline, 0);
  };

  useEffect(() => {
    // Initialize Tone.js players from config
    const initPlayers = async () => {
      const totalAssets =
        Object.keys(musicSystem.tracks).length +
        Object.keys(musicSystem.transitions).length;
      let loadedCount = 0;

      const checkAllLoaded = () => {
        loadedCount++;
        if (loadedCount === totalAssets) {
          console.log("All assets loaded");
          setIsLoaded(true);
        }
      };

      // Create players for all tracks with volume, envelope, and filter nodes
      Object.entries(musicSystem.tracks).forEach(([trackId, trackConfig]) => {
        // Create filter node (lowpass filter)
        const filter = new Tone.Filter({
          frequency: 200,
          type: "lowpass",
          rolloff: -24,
          Q: 0.7, // No resonance (flat response)
        });
        filterNodesRef.current[trackId] = filter;

        // Create envelope gain node (for AR envelope control)
        const envelopeGain = new Tone.Gain(1);
        envelopeNodesRef.current[trackId] = envelopeGain;

        // Create volume node (for parameter control)
        const volumeNode = new Tone.Volume(0);
        volumeNodesRef.current[trackId] = volumeNode;

        // Connect chain: Player -> EnvelopeGain -> Volume -> Filter -> Destination
        envelopeGain.connect(volumeNode);
        volumeNode.connect(filter);
        filter.toDestination();

        const player = new Tone.Player({
          url: trackConfig.url,
          loop: false,
          onload: checkAllLoaded,
        }).connect(envelopeGain);

        playersRef.current[trackId] = player;

        console.log(
          `Created audio chain for ${trackId}: Player -> EnvelopeGain -> Volume -> Filter -> Destination`,
        );
      });

      // Create players for all transitions
      Object.entries(musicSystem.transitions).forEach(
        ([transitionId, transitionConfig]) => {
          transitionPlayersRef.current[transitionId] = new Tone.Player({
            url: transitionConfig.url,
            loop: false,
            onload: checkAllLoaded,
          }).toDestination();
        },
      );

      // Set BPM
      Tone.Transport.bpm.value = musicSystem.bpm;
    };

    initPlayers();

    // Cleanup
    return () => {
      // Dispose all track players
      Object.values(playersRef.current).forEach((player) => {
        if (player) player.dispose();
      });

      // Dispose all transition players
      Object.values(transitionPlayersRef.current).forEach((player) => {
        if (player) player.dispose();
      });

      if (scheduledEventRef.current) {
        Tone.Transport.clear(scheduledEventRef.current);
      }
      Tone.Transport.stop();
      Tone.Transport.cancel();
    };
  }, []);

  // Helper function to find which loop region contains a timeline position
  const findLoopRegion = (position) => {
    const rootTimeline = musicSystem.timelines[musicSystem.rootTimeline];
    if (!rootTimeline) return null;
    return rootTimeline.loopRegions.find(
      (region) => position >= region.start && position < region.end,
    );
  };

  // Helper function to interpolate volume based on parameter and volume nodes
  const interpolateVolume = (param, volumeNodes) => {
    if (!volumeNodes || volumeNodes.length === 0) return 0;

    // Sort nodes by value
    const sorted = [...volumeNodes].sort((a, b) => a.value - b.value);

    // If param is below first node, return first volume
    if (param <= sorted[0].value) return sorted[0].volume;

    // If param is above last node, return last volume
    if (param >= sorted[sorted.length - 1].value)
      return sorted[sorted.length - 1].volume;

    // Find the two nodes to interpolate between
    for (let i = 0; i < sorted.length - 1; i++) {
      const node1 = sorted[i];
      const node2 = sorted[i + 1];

      if (param >= node1.value && param <= node2.value) {
        // Linear interpolation between nodes
        const range = node2.value - node1.value;
        const position = (param - node1.value) / range;
        const volumeRange = node2.volume - node1.volume;
        return node1.volume + volumeRange * position;
      }
    }

    return 0;
  };

  // Helper function to interpolate filter frequency based on parameter
  const interpolateFrequency = (param, filterNodes) => {
    if (!filterNodes || filterNodes.length === 0) return 20000;

    // Sort nodes by value
    const sorted = [...filterNodes].sort((a, b) => a.value - b.value);

    // If param is below first node, return first frequency
    if (param <= sorted[0].value) return sorted[0].frequency;

    // If param is above last node, return last frequency
    if (param >= sorted[sorted.length - 1].value)
      return sorted[sorted.length - 1].frequency;

    // Find the two nodes to interpolate between
    for (let i = 0; i < sorted.length - 1; i++) {
      const node1 = sorted[i];
      const node2 = sorted[i + 1];

      if (param >= node1.value && param <= node2.value) {
        // Linear interpolation between nodes
        const range = node2.value - node1.value;
        const position = (param - node1.value) / range;
        const freqRange = node2.frequency - node1.frequency;
        return node1.frequency + freqRange * position;
      }
    }

    return 20000;
  };

  // Update volumes when parameterA changes
  useEffect(() => {
    const resolvedTracks = getResolvedTracks();
    resolvedTracks.forEach((track) => {
      if (track.volumeParams.paramA && volumeNodesRef.current[track.trackId]) {
        const targetVolume = interpolateVolume(
          parameterA,
          track.volumeParams.paramA,
        );
        volumeNodesRef.current[track.trackId].volume.value = targetVolume;
      }
    });
  }, [parameterA]);

  // Update filters when parameterB changes
  useEffect(() => {
    const resolvedTracks = getResolvedTracks();
    resolvedTracks.forEach((track) => {
      if (track.filterParams.paramB && filterNodesRef.current[track.trackId]) {
        const targetFrequency = interpolateFrequency(
          parameterB,
          track.filterParams.paramB,
        );
        filterNodesRef.current[track.trackId].frequency.value = targetFrequency;
      }
    });
  }, [parameterB]);

  // Execute a transition with precise timing using Tone.js scheduling
  // idealStartTime: the audio context time when the transition should have started
  // overshoot: how far past the trigger we detected (for timer adjustments)
  const executeTransitionWithTiming = (
    transitionId,
    transitionConfig,
    idealStartTime,
    overshoot,
  ) => {
    const callId = Math.random().toString(36).substr(2, 9);

    // Get resolved tracks to find targets at the target position
    const resolvedTracks = getResolvedTracks();
    const targetTracks = resolvedTracks.filter((track) => {
      return (
        transitionConfig.targetPosition >= track.startTime &&
        transitionConfig.targetPosition < track.endTime
      );
    });

    // activeTracks contains trackObjIds, we need to map to trackIds for stopping players
    const currentTrackObjIds = [...activeTracks];
    const transitionPlayer = transitionPlayersRef.current[transitionId];
    const now = Tone.context.currentTime;
    const scheduledTime = Tone.now(); // Includes lookahead for consistent timing with other tracks
    const lookahead = scheduledTime - now;

    // Calculate how far into the transition we should be right now
    const timeIntoTransition = now - idealStartTime;

    // DETAILED EXECUTION DEBUG
    console.log(
      `\n========== TRANSITION EXECUTION DEBUG [${callId}] ==========`,
    );
    console.log(`[EXEC] Tone.context.currentTime (now): ${now.toFixed(6)}s`);
    console.log(
      `[EXEC] Tone.now() (with lookahead): ${scheduledTime.toFixed(6)}s`,
    );
    console.log(`[EXEC] Lookahead: ${(lookahead * 1000).toFixed(3)}ms`);
    console.log(
      `[EXEC] idealStartTime passed in: ${idealStartTime.toFixed(6)}s`,
    );
    console.log(
      `[EXEC] overshoot passed in: ${overshoot.toFixed(6)}s (${(overshoot * 1000).toFixed(3)}ms)`,
    );
    console.log(
      `[EXEC] timeIntoTransition (now - idealStartTime): ${timeIntoTransition.toFixed(6)}s (${(timeIntoTransition * 1000).toFixed(3)}ms)`,
    );
    console.log(
      `[EXEC] transitionConfig.startOffset: ${transitionConfig.startOffset || 0}s`,
    );

    // Start the transition audio at the correct offset
    // Use Tone.now() ('+0') to match how original tracks are started - this includes lookahead
    const transitionStartOffset =
      (transitionConfig.startOffset || 0) + timeIntoTransition;
    const actualOffset = Math.max(0, transitionStartOffset);

    console.log(
      `[EXEC] Calculated transitionStartOffset: ${transitionStartOffset.toFixed(6)}s`,
    );
    console.log(
      `[EXEC] Actual offset used (after max 0): ${actualOffset.toFixed(6)}s`,
    );
    console.log(
      `[EXEC] 🎵 STARTING TRANSITION AUDIO with lookahead at offset ${actualOffset.toFixed(3)}s`,
    );
    console.log(`=========================================================\n`);

    // Use '+0' to match how original tracks start (includes lookahead for consistent timing)
    transitionPlayer.start("+0", actualOffset);

    // Set transition state
    setIsTransitioning(true);
    setPendingTransition(false);
    setActiveTransition({
      id: transitionId,
      startTime: idealStartTime,
      duration: transitionConfig.duration,
    });

    // Get timing parameters
    const prevPosEnd =
      transitionConfig.prevPosEnd !== undefined
        ? transitionConfig.prevPosEnd
        : transitionConfig.duration;
    const nextPosStart =
      transitionConfig.nextPosStart !== undefined
        ? transitionConfig.nextPosStart
        : transitionConfig.duration;

    // Calculate remaining time for each event (accounting for time already elapsed)
    const remainingPrevPosEnd = Math.max(0, prevPosEnd - timeIntoTransition);
    const remainingNextPosStart = Math.max(
      0,
      nextPosStart - timeIntoTransition,
    );
    const remainingDuration = Math.max(
      0,
      transitionConfig.duration - timeIntoTransition,
    );

    console.log(
      `[${callId}] ⏱️ prevPosEnd: ${prevPosEnd}s (remaining: ${remainingPrevPosEnd.toFixed(3)}s), nextPosStart: ${nextPosStart}s (remaining: ${remainingNextPosStart.toFixed(3)}s)`,
    );

    // Stop previous tracks
    setTimeout(() => {
      console.log(`[${callId}] ⏹️ Stopping previous tracks`);
      currentTrackObjIds.forEach((trackObjId) => {
        const trackInfo = resolvedTracks.find(
          (t) => t.trackObjId === trackObjId,
        );
        if (trackInfo) {
          const player = playersRef.current[trackInfo.trackId];
          if (player && player.state === "started") {
            player.stop();
          }
        }
      });
    }, remainingPrevPosEnd * 1000);

    // Start target tracks
    setTimeout(() => {
      console.log(
        `[${callId}] ▶️ Starting target tracks at position ${transitionConfig.targetPosition}`,
      );

      const newActiveTrackObjIds = [];
      targetTracks.forEach((track) => {
        const player = playersRef.current[track.trackId];
        if (player) {
          const offsetIntoTrack =
            transitionConfig.targetPosition - track.startTime;
          const playbackOffset = track.playbackOffset + offsetIntoTrack;
          // Use '+0' for consistent timing with other track starts (includes lookahead)
          player.start("+0", playbackOffset);
          newActiveTrackObjIds.push(track.trackObjId); // Store trackObjId, not trackId
          console.log(
            `[${callId}] ▶️ Started ${track.trackObjId} (${track.trackId}) at offset ${playbackOffset.toFixed(2)}s`,
          );
        }
      });

      setCurrentPosition(transitionConfig.targetPosition);
      setActiveTracks(newActiveTrackObjIds);
      Tone.Transport.seconds = 0;
      absoluteTimelineStartRef.current =
        Tone.now() - transitionConfig.targetPosition;
      console.log(
        `[${callId}] 📍 Timeline position updated to ${transitionConfig.targetPosition}`,
      );
    }, remainingNextPosStart * 1000);

    // Mark transition complete
    setTimeout(() => {
      console.log(`[${callId}] ✅ TRANSITION COMPLETE`);
      setIsTransitioning(false);
      setActiveTransition(null);
    }, remainingDuration * 1000);
  };

  // Execute a transition (called by button or auto-trigger)
  // Defined here so it's available in the position tracking useEffect
  // overshoot: compensation for detection delay (how far past trigger we detected it)
  const executeTransitionAtPosition = (
    transitionId,
    transitionConfig,
    immediate = false,
    overshoot = 0,
  ) => {
    const callId = Math.random().toString(36).substr(2, 9);
    console.log(`\n🔵 [${callId}] executeTransitionAtPosition CALLED`);
    console.log(
      `[${callId}] transitionId: ${transitionId}, immediate: ${immediate}, overshoot: ${overshoot.toFixed(3)}s`,
    );

    // Get resolved tracks to find targets at the target position
    const resolvedTracks = getResolvedTracks();
    const targetTracks = resolvedTracks.filter((track) => {
      return (
        transitionConfig.targetPosition >= track.startTime &&
        transitionConfig.targetPosition < track.endTime
      );
    });

    console.log("Transition config:", transitionConfig);
    console.log(
      `Target timeline position: ${transitionConfig.targetPosition}s, Target tracks: ${targetTracks.map((t) => t.trackId).join(", ")}`,
    );

    // Get current timeline position
    const relativeTime = Tone.Transport.seconds;
    const currentTimelinePos = currentPosition + relativeTime;

    // Calculate time until transition (0 if immediate/auto-triggered at position)
    const timeUntilTransition = immediate
      ? 0
      : Math.max(0, transitionConfig.triggerPosition - currentTimelinePos);
    console.log(
      `[${callId}] ⏰ Time until transition: ${timeUntilTransition.toFixed(3)}s`,
    );

    // activeTracks contains trackObjIds
    const currentTrackObjIds = [...activeTracks];
    const transitionPlayer = transitionPlayersRef.current[transitionId];

    // Calculate absolute audio context time for the transition
    const now = Tone.context.currentTime;
    const transitionStartTime = now + timeUntilTransition;

    console.log(
      `[${callId}] 📅 Transition starts at absolute time ${transitionStartTime.toFixed(3)}`,
    );

    // Execute the transition
    const doTransition = () => {
      const time = Tone.context.currentTime;
      console.log(`\n🎬 [${callId}] === TRANSITION EXECUTING ===`);
      console.log(`[${callId}] Audio Context Time: ${time.toFixed(3)}`);

      // Play transition track with overshoot compensation
      // Start the audio at the overshoot offset so it's in sync with where playback should be
      // Use '+0' to match how other tracks start (includes lookahead for consistent timing)
      const transitionStartOffset =
        (transitionConfig.startOffset || 0) + overshoot;
      transitionPlayer.start("+0", transitionStartOffset);
      console.log(
        `[${callId}] 🎵 Transition audio started at offset ${transitionStartOffset.toFixed(3)}s (overshoot compensation: ${overshoot.toFixed(3)}s)`,
      );

      // Set transition state and track the transition for visualization
      // Adjust startTime backwards by overshoot so progress displays correctly
      setIsTransitioning(true);
      setPendingTransition(false);
      setActiveTransition({
        id: transitionId,
        startTime: time - overshoot,
        duration: transitionConfig.duration,
      });

      // Get timing parameters (adjusted for overshoot)
      const prevPosEnd =
        transitionConfig.prevPosEnd !== undefined
          ? transitionConfig.prevPosEnd
          : transitionConfig.duration;
      const nextPosStart =
        transitionConfig.nextPosStart !== undefined
          ? transitionConfig.nextPosStart
          : transitionConfig.duration;

      // Adjust timings for overshoot - we're already `overshoot` seconds into the transition
      const adjustedPrevPosEnd = Math.max(0, prevPosEnd - overshoot);
      const adjustedNextPosStart = Math.max(0, nextPosStart - overshoot);

      console.log(
        `[${callId}] ⏱️ prevPosEnd: ${prevPosEnd}s (adjusted: ${adjustedPrevPosEnd.toFixed(3)}s), nextPosStart: ${nextPosStart}s (adjusted: ${adjustedNextPosStart.toFixed(3)}s)`,
      );

      // Stop previous tracks after prevPosEnd (adjusted for overshoot)
      setTimeout(() => {
        console.log(`[${callId}] ⏹️ Stopping previous tracks`);
        currentTrackObjIds.forEach((trackObjId) => {
          const trackInfo = resolvedTracks.find(
            (t) => t.trackObjId === trackObjId,
          );
          if (trackInfo) {
            const player = playersRef.current[trackInfo.trackId];
            if (player && player.state === "started") {
              player.stop();
            }
          }
        });
      }, adjustedPrevPosEnd * 1000);

      // Start target tracks at nextPosStart (adjusted for overshoot)
      setTimeout(() => {
        console.log(
          `[${callId}] ▶️ Starting target tracks at position ${transitionConfig.targetPosition}`,
        );

        // Calculate offset into tracks based on target position
        const newActiveTrackObjIds = [];
        targetTracks.forEach((track) => {
          const player = playersRef.current[track.trackId];
          if (player) {
            const offsetIntoTrack =
              transitionConfig.targetPosition - track.startTime;
            const playbackOffset = track.playbackOffset + offsetIntoTrack;
            // Use '+0' for consistent timing with other track starts (includes lookahead)
            player.start("+0", playbackOffset);
            newActiveTrackObjIds.push(track.trackObjId); // Store trackObjId, not trackId
            console.log(
              `[${callId}] ▶️ Started ${track.trackObjId} (${track.trackId}) at offset ${playbackOffset.toFixed(2)}s`,
            );
          }
        });

        // Update timeline position and active tracks
        setCurrentPosition(transitionConfig.targetPosition);
        setActiveTracks(newActiveTrackObjIds);
        Tone.Transport.seconds = 0;
        absoluteTimelineStartRef.current =
          Tone.now() - transitionConfig.targetPosition;
        console.log(
          `[${callId}] 📍 Timeline position updated to ${transitionConfig.targetPosition}`,
        );
      }, adjustedNextPosStart * 1000);

      // Mark transition as complete when duration ends (adjusted for overshoot)
      const adjustedDuration = Math.max(
        0,
        transitionConfig.duration - overshoot,
      );
      setTimeout(() => {
        console.log(`[${callId}] ✅ TRANSITION COMPLETE`);
        setIsTransitioning(false);
        setActiveTransition(null);
      }, adjustedDuration * 1000);
    };

    if (immediate) {
      doTransition();
    } else {
      // Schedule for later
      const timeoutMs = timeUntilTransition * 1000;
      scheduledEventRef.current = setTimeout(doTransition, timeoutMs);
      console.log(
        `[${callId}] ⏲️ setTimeout scheduled for ${timeoutMs.toFixed(0)}ms`,
      );
    }
  };

  // Track live timeline position while playing and handle looping
  useEffect(() => {
    if (isPlaying) {
      const updatePosition = () => {
        // Get current playback time relative to track start
        const relativeTime = Tone.Transport.seconds;
        // Convert to timeline position (this resets during loops)
        let timelinePos = currentPosition + relativeTime;

        // Get absolute playback time (never resets during loops)
        const absolutePlaybackTime =
          Tone.context.currentTime - absoluteTimelineStartRef.current;

        // Calculate individual timeline positions
        const newTimelinePositions = {};
        const resolvedTracks = getResolvedTracks();

        // Calculate timeline positions
        Object.entries(musicSystem.timelines).forEach(
          ([timelineId, timeline]) => {
            if (timelineId === musicSystem.rootTimeline) {
              // Root timeline always shows absolute playback time
              newTimelinePositions[timelineId] = absolutePlaybackTime;
            } else {
              // For nested timelines, find the container trackObject that references this timeline
              const containerObj = Object.values(musicSystem.trackObjects).find(
                (obj) =>
                  obj.type === "timeline" && obj.timelineRef === timelineId,
              );

              if (
                containerObj &&
                timeline.loopRegions &&
                timeline.loopRegions.length > 0
              ) {
                const loopRegion = timeline.loopRegions[0];
                const loopDuration = loopRegion.end - loopRegion.start;

                // Check if we're within the container's active period on main timeline
                if (
                  timelinePos >= containerObj.startTime &&
                  timelinePos < containerObj.endTime
                ) {
                  // Calculate position within the nested timeline
                  const posInContainer = timelinePos - containerObj.startTime;
                  const posInLoop =
                    (posInContainer % loopDuration) + loopRegion.start;
                  newTimelinePositions[timelineId] = posInLoop;
                } else {
                  // Outside container's active period - show start position
                  newTimelinePositions[timelineId] = loopRegion.start;
                }
              } else {
                // For non-looping nested timelines
                newTimelinePositions[timelineId] = absolutePlaybackTime;
              }
            }
          },
        );

        setTimelinePositions(newTimelinePositions);

        // Check if any new tracks should start at this timeline position
        if (!isTransitioning && !pendingTransition) {
          // Find tracks that should be playing now but aren't
          // Note: activeTracks stores trackObjIds to handle multiple trackObjects referencing same track
          const shouldBePlaying = resolvedTracks.filter((track) => {
            if (activeTracks.includes(track.trackObjId)) {
              return false;
            }

            // For tracks in nested timelines with loop regions
            if (
              track.timelineId !== musicSystem.rootTimeline &&
              track.absoluteLoopRegions &&
              track.absoluteLoopRegions.length > 0
            ) {
              const loopRegion = track.absoluteLoopRegions[0];
              const loopDuration = loopRegion.end - loopRegion.start;

              // Use the nested container's actual end time if available, otherwise fall back to loop region end
              const containerEndTime =
                track.nestedContainerEndTime !== Infinity
                  ? track.nestedContainerEndTime
                  : loopRegion.end;

              // Check if we're within the nested timeline's active period on main timeline
              // Release extends BEYOND container end, so don't restart once we've passed container end
              const outsideRange =
                timelinePos < loopRegion.start ||
                timelinePos >= containerEndTime;
              if (outsideRange) {
                return false;
              }

              // Calculate position within the nested timeline (relative to when the container starts)
              const posInNestedTimeline = timelinePos - loopRegion.start;

              // Get position within the current loop cycle
              const posInLoop = posInNestedTimeline % loopDuration;

              // The track should play if we're at a valid position in the loop cycle
              // (containerEndTime check above already ensures we're within the container's duration)
              return posInLoop >= 0 && posInLoop < loopDuration - 0.016;
            }

            // For regular tracks (no nested loops)
            // Release extends beyond end time, so don't restart once past endTime
            return (
              timelinePos >= track.startTime && timelinePos < track.endTime
            );
          });

          // Start any tracks that just crossed their startTime
          if (shouldBePlaying.length > 0) {
            const now = Tone.now(); // Use Tone.now() for consistent timing (includes lookahead)
            shouldBePlaying.forEach((track) => {
              const player = playersRef.current[track.trackId];

              // For looping tracks, we need to stop first then restart from correct offset
              // This handles the case where the previous stop hasn't taken effect yet
              if (player.state === "started") {
                player.stop();
              }

              // Reset loop mode (may have been enabled during previous release)
              player.loop = false;

              // Calculate how far into the track we should start
              let offset;
              if (
                track.timelineId !== musicSystem.rootTimeline &&
                track.absoluteLoopRegions &&
                track.absoluteLoopRegions.length > 0
              ) {
                // For looping nested timelines, calculate offset within the loop
                const loopRegion = track.absoluteLoopRegions[0];
                const posInNestedTimeline = timelinePos - loopRegion.start;
                const loopDuration = loopRegion.end - loopRegion.start;
                const posInLoop = posInNestedTimeline % loopDuration;
                offset = track.playbackOffset + posInLoop;
              } else {
                // For regular tracks
                offset = track.playbackOffset + (timelinePos - track.startTime);
              }

              // Apply attack envelope if present
              const envelopeGain = envelopeNodesRef.current[track.trackId];
              if (track.envelope && track.envelope.attack > 0) {
                // Start from 0 and ramp to 1 over attack time
                envelopeGain.gain.setValueAtTime(0, now);
                envelopeGain.gain.linearRampToValueAtTime(
                  1,
                  now + track.envelope.attack,
                );
                console.log(
                  `🎵 Auto-started track ${track.trackObjId} (${track.trackId}) with ${track.envelope.attack}s attack at timeline ${timelinePos.toFixed(2)}s`,
                );
              } else {
                // No attack, start at full volume
                envelopeGain.gain.setValueAtTime(1, now);
                console.log(
                  `🎵 Auto-started track ${track.trackObjId} (${track.trackId}) at timeline ${timelinePos.toFixed(2)}s (offset: ${offset.toFixed(2)}s)`,
                );
              }

              // Use '+0' for consistent timing with all other track starts
              player.start("+0", offset);
            });

            // Store trackObjIds (not trackIds) to handle multiple trackObjects referencing same track
            setActiveTracks([
              ...activeTracks,
              ...shouldBePlaying.map((t) => t.trackObjId),
            ]);
          }

          // Check for tracks that should stop
          // Note: activeTracks stores trackObjIds
          const shouldStop = activeTracks.filter((trackObjId) => {
            const trackInfo = resolvedTracks.find(
              (t) => t.trackObjId === trackObjId,
            );
            if (!trackInfo) return false;

            // For nested timelines with loops
            if (
              trackInfo.timelineId !== musicSystem.rootTimeline &&
              trackInfo.absoluteLoopRegions &&
              trackInfo.absoluteLoopRegions.length > 0
            ) {
              const loopRegion = trackInfo.absoluteLoopRegions[0];
              const loopDuration = loopRegion.end - loopRegion.start;

              // Use the nested container's actual end time
              const containerEndTime =
                trackInfo.nestedContainerEndTime !== Infinity
                  ? trackInfo.nestedContainerEndTime
                  : loopRegion.end;

              // Trigger stop at container end - release will extend BEYOND container boundary
              if (timelinePos >= containerEndTime) {
                return true;
              }

              // Calculate position within nested timeline
              const posInNestedTimeline = timelinePos - loopRegion.start;

              // Get position within the current loop cycle
              const posInLoop = posInNestedTimeline % loopDuration;

              // Detect if we just crossed a loop boundary
              // We need to check if the previous frame was in the previous cycle
              const prevPosInNestedTimeline = posInNestedTimeline - 0.016; // ~60fps
              const prevCycle = Math.floor(
                prevPosInNestedTimeline / loopDuration,
              );
              const currentCycle = Math.floor(
                posInNestedTimeline / loopDuration,
              );

              // If we moved to a new cycle, stop the track so it can restart from 0
              if (currentCycle > prevCycle && posInLoop < 0.1) {
                console.log(
                  `🔁 Loop boundary crossed for ${trackObjId}: cycle ${prevCycle} -> ${currentCycle}, posInLoop: ${posInLoop.toFixed(3)}s`,
                );
                return true;
              }

              // Nested looping tracks only stop based on container end or loop boundary
              // Don't fall through to regular endTime check
              return false;
            }

            // For regular tracks, trigger stop at endTime
            // Release will extend beyond endTime
            return timelinePos >= trackInfo.endTime;
          });

          if (shouldStop.length > 0) {
            const now = Tone.context.currentTime;
            shouldStop.forEach((trackObjId) => {
              const trackInfo = resolvedTracks.find(
                (t) => t.trackObjId === trackObjId,
              );
              if (!trackInfo) return;

              // Use trackId to get the actual player
              const player = playersRef.current[trackInfo.trackId];
              const envelopeGain = envelopeNodesRef.current[trackInfo.trackId];

              if (trackInfo.envelope && trackInfo.envelope.release > 0) {
                // Apply release envelope: ramp from current value to 0 over release time
                envelopeGain.gain.cancelScheduledValues(now);
                envelopeGain.gain.setValueAtTime(envelopeGain.gain.value, now);
                envelopeGain.gain.linearRampToValueAtTime(
                  0,
                  now + trackInfo.envelope.release,
                );

                // For nested looping tracks, enable looping so audio continues during release fade
                // This handles the case where audio file length equals loop duration
                if (
                  trackInfo.timelineId !== musicSystem.rootTimeline &&
                  trackInfo.absoluteLoopRegions &&
                  trackInfo.absoluteLoopRegions.length > 0
                ) {
                  const loopRegion = trackInfo.absoluteLoopRegions[0];
                  const loopDuration = loopRegion.end - loopRegion.start;

                  // Enable looping with correct loop points
                  player.loop = true;
                  player.loopStart = trackInfo.playbackOffset;
                  player.loopEnd = trackInfo.playbackOffset + loopDuration;
                  console.log(
                    `🔁 Enabled looping during release: ${trackInfo.playbackOffset}s to ${trackInfo.playbackOffset + loopDuration}s`,
                  );
                }

                // Stop the player after release completes
                player.stop(now + trackInfo.envelope.release);
                console.log(
                  `🛑 Track ${trackObjId} (${trackInfo.trackId}) releasing over ${trackInfo.envelope.release}s (triggered at timeline ${timelinePos.toFixed(2)}s)`,
                );
              } else {
                // No release, stop immediately
                player.stop(now);

                // For looping tracks, log the loop position
                if (
                  trackInfo.timelineId !== musicSystem.rootTimeline &&
                  trackInfo.absoluteLoopRegions
                ) {
                  const loopRegion = trackInfo.absoluteLoopRegions[0];
                  const posInNestedTimeline = timelinePos - loopRegion.start;
                  const loopDuration = loopRegion.end - loopRegion.start;
                  const posInLoop = posInNestedTimeline % loopDuration;
                  console.log(
                    `🔁 Stopped ${trackObjId} at loop position ${posInLoop.toFixed(2)}s - will restart at beginning of next cycle`,
                  );
                } else {
                  console.log(
                    `🛑 Auto-stopped ${trackObjId} (${trackInfo.trackId}) at timeline position ${timelinePos.toFixed(2)}s`,
                  );
                }
              }
            });

            setActiveTracks(
              activeTracks.filter((id) => !shouldStop.includes(id)),
            );
          }

          // Check if main timeline has reached loop end and loop back
          const mainTimeline = musicSystem.timelines[musicSystem.rootTimeline];
          if (
            mainTimeline &&
            mainTimeline.loopRegions &&
            mainTimeline.loopRegions.length > 0
          ) {
            const loopRegion = mainTimeline.loopRegions[0];
            if (absolutePlaybackTime >= loopRegion.end) {
              console.log(
                `🔁 Main timeline reached loop end at ${loopRegion.end}s - looping back to ${loopRegion.start}s`,
              );

              // Stop all currently playing tracks
              // Note: activeTracks contains trackObjIds, so we need to look up the trackId
              activeTracks.forEach((trackObjId) => {
                const trackInfo = resolvedTracks.find(
                  (t) => t.trackObjId === trackObjId,
                );
                if (trackInfo) {
                  const player = playersRef.current[trackInfo.trackId];
                  if (player) {
                    player.stop();
                  }
                }
              });

              // Clear active tracks so they can restart
              setActiveTracks([]);

              // Reset timeline position to loop start
              setCurrentPosition(loopRegion.start);
              Tone.Transport.seconds = 0;
              absoluteTimelineStartRef.current =
                Tone.context.currentTime - loopRegion.start;

              // Don't stop playback - it will continue and auto-start tracks at loop start position
              return;
            }
          } else if (
            mainTimeline &&
            mainTimeline.length &&
            absolutePlaybackTime >= mainTimeline.length
          ) {
            // Fallback: if no loop regions, stop at timeline length
            console.log(
              `🏁 Main timeline reached end at ${mainTimeline.length}s - stopping playback`,
            );

            Object.values(playersRef.current).forEach((player) => {
              if (player) player.stop();
            });

            Tone.Transport.stop();
            setIsPlaying(false);
            setActiveTracks([]);
            return;
          }
        }

        // Check if armed transition should execute (cursor reached trigger position)
        if (!isTransitioning && pendingTransition && transitionParam > 0) {
          const matchingTransition = Object.entries(
            musicSystem.transitions,
          ).find(([_, config]) => {
            const matchesParam = config.targetIndex === transitionParam;
            // Check if cursor has reached or passed the trigger position
            const atTrigger =
              config.triggerPosition !== undefined &&
              timelinePos >= config.triggerPosition;
            return matchesParam && atTrigger;
          });

          if (matchingTransition) {
            const [transitionId, transitionConfig] = matchingTransition;
            const overshoot = timelinePos - transitionConfig.triggerPosition;

            console.log(
              `\n⚡ Armed transition triggered - cursor reached trigger at ${timelinePos.toFixed(3)}s (overshoot: ${overshoot.toFixed(3)}s)`,
            );
            executeTransitionAtPosition(
              transitionId,
              transitionConfig,
              true,
              overshoot,
            );
          }
        }

        // Update transition progress for visualization
        if (activeTransition) {
          const elapsed = Tone.context.currentTime - activeTransition.startTime;
          const progress = Math.min(1, elapsed / activeTransition.duration);
          setTransitionProgress(progress);
        }

        setLiveTimelinePos(timelinePos);
        animationFrameRef.current = requestAnimationFrame(updatePosition);
      };

      updatePosition();

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    } else {
      setLiveTimelinePos(currentPosition);
    }
  }, [
    isPlaying,
    currentPosition,
    isTransitioning,
    pendingTransition,
    transitionParam,
    activeTracks,
    activeTransition,
  ]);

  const handleStart = async () => {
    await Tone.start();

    if (isLoaded) {
      // Reset system before playing
      Object.values(playersRef.current).forEach((player) => {
        if (player) player.stop();
      });
      Object.values(transitionPlayersRef.current).forEach((player) => {
        if (player) player.stop();
      });
      Tone.Transport.stop();
      Tone.Transport.position = 0;
      setIsTransitioning(false);
      setPendingTransition(false);
      setCurrentPosition(musicSystem.initialPosition);
      setActiveTracks([]);
      setActiveTransition(null);
      setTransitionProgress(0);
      setTransitionParam(0);
      if (scheduledEventRef.current) {
        clearTimeout(scheduledEventRef.current);
        scheduledEventRef.current = null;
      }

      // Get resolved tracks and find which should be playing at initial position
      const resolvedTracks = getResolvedTracks();
      const initialTracks = resolvedTracks.filter((track) => {
        return (
          musicSystem.initialPosition >= track.startTime &&
          musicSystem.initialPosition < track.endTime
        );
      });

      // Start playback from initial position
      setIsPlaying(true);
      Tone.Transport.start();
      timelineStartRef.current = Tone.context.currentTime;
      absoluteTimelineStartRef.current =
        Tone.context.currentTime - musicSystem.initialPosition;

      // DEBUG: Log playback start reference values
      console.log(`\n========== PLAYBACK STARTED ==========`);
      console.log(
        `[START] Tone.context.currentTime: ${Tone.context.currentTime.toFixed(6)}s`,
      );
      console.log(
        `[START] initialPosition: ${musicSystem.initialPosition.toFixed(6)}s`,
      );
      console.log(
        `[START] absoluteTimelineStartRef: ${absoluteTimelineStartRef.current.toFixed(6)}s`,
      );
      console.log(
        `[START] Transition trigger at: ${musicSystem.transitions.transition_to_b?.triggerPosition || "N/A"}s`,
      );
      console.log(`=======================================\n`);

      // Start tracks that should be playing at initial position
      if (initialTracks.length > 0) {
        const trackObjIds = [];

        const now = Tone.context.currentTime;
        initialTracks.forEach((track) => {
          const player = playersRef.current[track.trackId];
          const envelopeGain = envelopeNodesRef.current[track.trackId];

          // Calculate offset based on initial position within the track
          const offsetIntoTrack = musicSystem.initialPosition - track.startTime;
          const playbackOffset = track.playbackOffset + offsetIntoTrack;

          // Apply attack envelope if present (only if starting from beginning of track)
          if (
            track.envelope &&
            track.envelope.attack > 0 &&
            offsetIntoTrack === 0
          ) {
            envelopeGain.gain.setValueAtTime(0, now);
            envelopeGain.gain.linearRampToValueAtTime(
              1,
              now + track.envelope.attack,
            );
            console.log(
              `Started track: ${track.trackObjId} (${track.trackId}) with ${track.envelope.attack}s attack`,
            );
          } else {
            envelopeGain.gain.setValueAtTime(1, now);
            console.log(
              `Started track: ${track.trackObjId} (${track.trackId}) at position ${musicSystem.initialPosition.toFixed(2)}s (offset ${playbackOffset.toFixed(2)}s)`,
            );
          }

          player.start("+0", playbackOffset);
          trackObjIds.push(track.trackObjId); // Store trackObjId, not trackId
        });

        setActiveTracks(trackObjIds);
      }
    }
  };

  const handleStop = () => {
    // Calculate current timeline position before stopping
    const relativeTime = Tone.Transport.seconds;
    const stoppedPosition = currentPosition + relativeTime;

    // Stop all track players
    Object.values(playersRef.current).forEach((player) => {
      if (player) player.stop();
    });

    // Stop all transition players
    Object.values(transitionPlayersRef.current).forEach((player) => {
      if (player) player.stop();
    });

    Tone.Transport.stop();
    setIsPlaying(false);
    setIsTransitioning(false);
    setPendingTransition(false);

    // Save the current position (don't reset to 0)
    setCurrentPosition(stoppedPosition);

    // Clear any scheduled transitions
    if (scheduledEventRef.current) {
      clearTimeout(scheduledEventRef.current);
      scheduledEventRef.current = null;
    }
  };

  const handleReset = () => {
    // Stop all track players
    Object.values(playersRef.current).forEach((player) => {
      if (player) player.stop();
    });

    // Stop all transition players
    Object.values(transitionPlayersRef.current).forEach((player) => {
      if (player) player.stop();
    });

    Tone.Transport.stop();
    Tone.Transport.position = 0;
    setIsPlaying(false);
    setIsTransitioning(false);
    setPendingTransition(false);
    setCurrentPosition(musicSystem.initialPosition);
    setActiveTracks([]);
    setActiveTransition(null);
    setTransitionProgress(0);

    // Immediately reset visual timeline positions
    setLiveTimelinePos(musicSystem.initialPosition);
    const resetTimelinePositions = {};
    Object.keys(musicSystem.timelines).forEach((timelineId) => {
      resetTimelinePositions[timelineId] = musicSystem.initialPosition;
    });
    setTimelinePositions(resetTimelinePositions);

    // Clear any scheduled transitions
    if (scheduledEventRef.current) {
      clearTimeout(scheduledEventRef.current);
      scheduledEventRef.current = null;
    }
  };

  const handleTransition = () => {
    const callId = Math.random().toString(36).substr(2, 9);
    console.log(`\n🔵 [${callId}] handleTransition CALLED`);
    console.log(
      `[${callId}] isPlaying: ${isPlaying}, isTransitioning: ${isTransitioning}, pendingTransition: ${pendingTransition}, transitionParam: ${transitionParam}`,
    );

    if (!isPlaying || isTransitioning || pendingTransition) {
      console.log(`[${callId}] ❌ BLOCKED - returning early`);
      return;
    }

    // Check if transition param is set (0 = no transition)
    if (transitionParam === 0) {
      console.log(
        `[${callId}] ❌ Transition param is 0 - no transition will occur`,
      );
      return;
    }

    // Get current timeline position
    const relativeTime = Tone.Transport.seconds;
    const currentTimelinePos = currentPosition + relativeTime;

    console.log(
      `[${callId}] 📍 Current Timeline Pos: ${currentTimelinePos.toFixed(3)}`,
    );

    // Find a transition that matches the transitionParam (using triggerPosition)
    const matchingTransition = Object.entries(musicSystem.transitions).find(
      ([_, config]) => {
        const matchesParam = config.targetIndex === transitionParam;
        return matchesParam && config.triggerPosition !== undefined;
      },
    );

    if (!matchingTransition) {
      console.log(
        `[${callId}] ❌ No transition found for param ${transitionParam}`,
      );
      return;
    }

    const [transitionId, transitionConfig] = matchingTransition;
    const triggerPos = transitionConfig.triggerPosition;

    // Check if cursor has passed the trigger position
    if (currentTimelinePos >= triggerPos) {
      // Already past trigger: execute immediately
      const overshoot = currentTimelinePos - triggerPos;
      console.log(
        `[${callId}] ✅ Cursor past trigger (${triggerPos}s) - executing immediately with overshoot ${overshoot.toFixed(3)}s`,
      );
      executeTransitionAtPosition(
        transitionId,
        transitionConfig,
        true,
        overshoot,
      );
    } else {
      // Before trigger: arm the transition to execute when cursor reaches trigger
      console.log(
        `[${callId}] ⏳ Arming transition - will trigger at ${triggerPos}s`,
      );
      setPendingTransition(true);
    }
  };

  // Get transition trigger positions for display
  const transitionTriggers = Object.values(musicSystem.transitions).map(
    (t) => t.triggerPosition,
  );

  // Music state context value for visualizations
  const musicStateValue = useMemo(
    () => ({
      // State
      isPlaying,
      isLoaded,
      isTransitioning,
      pendingTransition,
      currentPosition,
      liveTimelinePos,
      timelinePositions,
      activeTracks,
      transitionProgress,
      transitionParam,
      parameterA,
      parameterB,
      activeTransition,
      musicSystem,
      // Game state
      gameState,
      intensity,
      layers,
      // Actions
      handleStart,
      handleStop,
      handleReset,
      handleTransition,
      setTransitionParam,
      setParameterA,
      setParameterB,
      setGameState,
    }),
    [
      isPlaying,
      isLoaded,
      isTransitioning,
      pendingTransition,
      currentPosition,
      liveTimelinePos,
      timelinePositions,
      activeTracks,
      transitionProgress,
      transitionParam,
      parameterA,
      parameterB,
      activeTransition,
      gameState,
      intensity,
      layers,
    ],
  );

  return (
    <MusicStateContext.Provider value={musicStateValue}>
      <div className="adaptive-music-player">
        {/* Custom Visualization */}
        {VisualizationComponent && (
          <div
            className="visualization-container"
            style={{ marginBottom: "20px" }}
          >
            <VisualizationComponent {...musicStateValue} />
          </div>
        )}

        {/* Current State Display - Above nodes */}
        <div
          style={{
            borderBottom: "1px solid #0f1218",
            padding: "16px 24px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "24px",
            }}
          >
            <p
              style={{
                fontSize: "10px",
                color: "#4a9eb5",
                textTransform: "uppercase",
                letterSpacing: "0.4em",
                margin: 0,
              }}
            >
              Current
            </p>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "200",
                color: "#d9dada",
                letterSpacing: "0.05em",
                margin: 0,
              }}
            >
              {STATE_DESCRIPTIONS[gameState].title}
            </h2>
            <span style={{ color: "#1a1f28" }}>|</span>
            <p
              style={{
                fontSize: "13px",
                color: "#6a7080",
                fontWeight: "300",
                margin: 0,
              }}
            >
              {STATE_DESCRIPTIONS[gameState].description}
            </p>
          </div>
        </div>

        {/* Main Content Area - States on left, Nodes in center */}
        <div
          style={{
            display: "flex",
            gap: "32px",
            padding: "20px",
            marginBottom: "20px",
          }}
        >
          {/* States - Vertical on left */}
          <div style={{ flexShrink: 0 }}>
            <h3
              style={{
                fontSize: "10px",
                color: "#6a7080",
                textTransform: "uppercase",
                letterSpacing: "0.25em",
                marginBottom: "16px",
              }}
            >
              States
            </h3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {GAME_STATES.map((state) => (
                <button
                  key={state}
                  onClick={() => setGameState(state)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 16px",
                    borderRadius: "9999px",
                    transition: "all 0.3s",
                    cursor: "pointer",
                    background: gameState === state ? "#0a0c12" : "#030509",
                    border:
                      gameState === state
                        ? "1px solid rgba(74,158,181,0.2)"
                        : "1px solid #0f1218",
                  }}
                >
                  <motion.div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      transition: "all 0.3s",
                      background: gameState === state ? "#4a9eb5" : "#1a1f28",
                      boxShadow:
                        gameState === state ? "0 0 8px #4a9eb5" : "none",
                    }}
                    animate={
                      gameState === state ? { scale: [1, 1.2, 1] } : undefined
                    }
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  <span
                    style={{
                      fontSize: "10px",
                      textTransform: "capitalize",
                      letterSpacing: "0.05em",
                      color: gameState === state ? "#d9dada" : "#6a7080",
                    }}
                  >
                    {state}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Flow Visualization - Absolute Position Grid */}
          <FlowVisualization
            isLoaded={isLoaded}
            isPlaying={isPlaying}
            isTransitioning={isTransitioning}
            pendingTransition={pendingTransition}
            activeTracks={activeTracks}
            liveTimelinePos={liveTimelinePos}
            transitionParam={transitionParam}
            onStart={handleStart}
            onStop={handleStop}
            onTransitionSelect={(targetIndex) => {
              if (isPlaying && !isTransitioning && !pendingTransition) {
                console.log(
                  `🎯 Transition selected: targetIndex ${targetIndex}`,
                );
                setTransitionParam(targetIndex);
                setPendingTransition(true);
              }
            }}
          />
        </div>

        {/* Active Layers - Below nodes */}
        <div
          style={{
            borderTop: "1px solid #0f1218",
            background: "#020407",
            padding: "24px",
          }}
        >
          <h3
            style={{
              fontSize: "10px",
              color: "#6a7080",
              textTransform: "uppercase",
              letterSpacing: "0.25em",
              marginBottom: "16px",
            }}
          >
            Active Layers
          </h3>
          <div style={{ display: "flex", gap: "12px" }}>
            {layers.map((layer, i) => (
              <motion.div
                key={layer.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "12px",
                  transition: "all 0.5s",
                  background: layer.active ? "#0a0c12" : "#030509",
                  border: layer.active
                    ? "1px solid rgba(74,158,181,0.1)"
                    : "1px solid transparent",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "10px",
                      fontWeight: "500",
                      letterSpacing: "0.05em",
                      color: layer.active ? "#d9dada" : "#2a2f38",
                    }}
                  >
                    {layer.name}
                  </span>
                  <motion.div
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: layer.active ? "#4a9eb5" : "#0f1218",
                    }}
                    animate={layer.active ? { scale: [1, 1.3, 1] } : undefined}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                </div>
                <div
                  style={{
                    height: "4px",
                    background: "#0f1218",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <motion.div
                    style={{
                      height: "100%",
                      background:
                        "linear-gradient(to right, rgba(74,158,181,0.4), #4a9eb5)",
                      borderRadius: "4px",
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${layer.volume * 100}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Dev View (optional) */}
        {showDevView && (
          <>
            <div className="player-info">
              <h3>Adaptive Music System (Dev View)</h3>
              <p>
                Active Tracks:{" "}
                {activeTracks.length > 0 ? activeTracks.join(", ") : "None"} |
                BPM: {musicSystem.bpm}
              </p>
              <p>
                Status:{" "}
                {isLoaded
                  ? isPlaying
                    ? isTransitioning
                      ? "Transitioning..."
                      : pendingTransition
                        ? "Pending Transition"
                        : "Playing"
                    : "Stopped"
                  : "Loading..."}
              </p>
            </div>

            {/* Visual Timeline Interface */}
            <div
              style={{
                margin: "20px 0",
                padding: "20px",
                background: "#1a1a1a",
                borderRadius: "8px",
                border: "1px solid #333",
              }}
            >
              {Object.entries(musicSystem.timelines).map(
                ([timelineId, timeline]) => {
                  const timelinePos = timelinePositions[timelineId] || 0;
                  const pixelsPerSecond = 20; // Reduced from 50 to fit ~30 seconds in view

                  return (
                    <div key={timelineId} style={{ marginBottom: "30px" }}>
                      {/* Timeline Header */}
                      <div
                        style={{
                          color: "#fff",
                          marginBottom: "10px",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        {timelineId} ({timelinePos.toFixed(2)}s)
                      </div>

                      {/* Timeline Container */}
                      <div
                        style={{
                          position: "relative",
                          height: "80px",
                          background: "#0a0a0a",
                          borderRadius: "4px",
                          border: "1px solid #333",
                          overflow: "hidden",
                        }}
                      >
                        {/* Time ruler */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "20px",
                            background: "#111",
                            borderBottom: "1px solid #333",
                          }}
                        >
                          {Array.from({
                            length: Math.ceil(timeline.length) + 1,
                          }).map((_, i) => (
                            <div
                              key={i}
                              style={{
                                position: "absolute",
                                left: `${i * pixelsPerSecond}px`,
                                top: 0,
                                width: "1px",
                                height: "8px",
                                background: "#555",
                              }}
                            >
                              <span
                                style={{
                                  position: "absolute",
                                  left: "3px",
                                  top: "0",
                                  fontSize: "10px",
                                  color: "#888",
                                }}
                              >
                                {i}s
                              </span>
                            </div>
                          ))}

                          {/* Transition Trigger Flags - yellow markers */}
                          {(() => {
                            // Get unique trigger positions
                            const triggers = Object.values(
                              musicSystem.transitions,
                            )
                              .filter(
                                (trans) => trans.triggerPosition !== undefined,
                              )
                              .map((trans) => trans.triggerPosition);
                            const uniqueTriggers = [...new Set(triggers)];

                            return uniqueTriggers.map((triggerPos, idx) => (
                              <div
                                key={idx}
                                style={{
                                  position: "absolute",
                                  left: `${triggerPos * pixelsPerSecond - 1}px`,
                                  top: "-8px",
                                  width: "2px",
                                  height: "28px",
                                  background: "#f0a500",
                                  boxShadow: "0 0 8px rgba(240, 165, 0, 0.6)",
                                  zIndex: 5,
                                }}
                              >
                                <span
                                  style={{
                                    position: "absolute",
                                    top: "-14px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    fontSize: "10px",
                                    color: "#f0a500",
                                    whiteSpace: "nowrap",
                                    fontWeight: "bold",
                                  }}
                                >
                                  ⚡ {triggerPos}s
                                </span>
                              </div>
                            ));
                          })()}
                        </div>

                        {/* Track Objects */}
                        {timeline.trackObjects.map((objId) => {
                          const trackObj = musicSystem.trackObjects[objId];
                          if (!trackObj) return null;

                          const startX = trackObj.startTime * pixelsPerSecond;
                          const width =
                            (trackObj.endTime - trackObj.startTime) *
                            pixelsPerSecond;
                          const isActive = activeTracks.includes(objId); // objId is the trackObjId

                          return (
                            <div
                              key={objId}
                              style={{
                                position: "absolute",
                                left: `${startX}px`,
                                top: "25px",
                                width: `${width}px`,
                                height: "40px",
                                background: isActive ? "#4a90e2" : "#2a2a2a",
                                border: isActive
                                  ? "2px solid #5aa3f0"
                                  : "1px solid #444",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontSize: "11px",
                                transition: "all 0.2s ease",
                              }}
                            >
                              {trackObj.trackRef || trackObj.timelineRef}
                            </div>
                          );
                        })}

                        {/* Loop Regions */}
                        {timeline.loopRegions &&
                          timeline.loopRegions.map((region, idx) => (
                            <div
                              key={idx}
                              style={{
                                position: "absolute",
                                left: `${region.start * pixelsPerSecond}px`,
                                top: "70px",
                                width: `${(region.end - region.start) * pixelsPerSecond}px`,
                                height: "5px",
                                background: "#666",
                                borderRadius: "2px",
                                opacity: 0.6,
                              }}
                            />
                          ))}

                        {/* Playhead */}
                        <div
                          style={{
                            position: "absolute",
                            left: `${(timelinePos % timeline.length) * pixelsPerSecond}px`,
                            top: 0,
                            width: "2px",
                            height: "100%",
                            background: "#ff4444",
                            boxShadow: "0 0 8px rgba(255, 68, 68, 0.8)",
                            transition: isPlaying ? "none" : "left 0.1s linear",
                            zIndex: 10,
                          }}
                        />
                      </div>

                      {/* Transition info */}
                      <div
                        style={{
                          marginTop: "10px",
                          paddingLeft: "10px",
                          fontSize: "11px",
                          color: "#888",
                        }}
                      >
                        {Object.entries(musicSystem.transitions)
                          .filter(
                            ([_, trans]) => trans.triggerPosition !== undefined,
                          )
                          .map(([transId, trans]) => (
                            <span
                              key={transId}
                              style={{ marginRight: "15px", color: "#f0a500" }}
                            >
                              ⚡ Trigger @ {trans.triggerPosition}s →{" "}
                              {trans.targetPosition}s (param {trans.targetIndex}
                              )
                            </span>
                          ))}
                      </div>
                    </div>
                  );
                },
              )}

              {/* Transition Timeline - shows when a transition is active */}
              {activeTransition && (
                <div style={{ marginBottom: "30px" }}>
                  {/* Transition Timeline Header */}
                  <div
                    style={{
                      color: "#f0a500",
                      marginBottom: "10px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "16px" }}>⚡</span>
                    Transition: {activeTransition.id} (
                    {(
                      transitionProgress *
                        musicSystem.transitions[activeTransition.id]
                          ?.duration || 0
                    ).toFixed(2)}
                    s /{" "}
                    {musicSystem.transitions[activeTransition.id]?.duration ||
                      0}
                    s)
                  </div>

                  {/* Transition Timeline Container */}
                  <div
                    style={{
                      position: "relative",
                      height: "60px",
                      background:
                        "linear-gradient(90deg, rgba(240, 165, 0, 0.1) 0%, rgba(240, 165, 0, 0.2) 100%)",
                      borderRadius: "4px",
                      border: "2px solid #f0a500",
                      overflow: "hidden",
                      boxShadow: "0 0 15px rgba(240, 165, 0, 0.3)",
                    }}
                  >
                    {/* Transition track visualization */}
                    {(() => {
                      const transConfig =
                        musicSystem.transitions[activeTransition.id];
                      if (!transConfig) return null;

                      const pixelsPerSecond = 50;
                      const duration = transConfig.duration;
                      const prevPosEnd =
                        transConfig.prevPosEnd !== undefined
                          ? transConfig.prevPosEnd
                          : duration;
                      const nextPosStart =
                        transConfig.nextPosStart !== undefined
                          ? transConfig.nextPosStart
                          : duration;

                      return (
                        <>
                          {/* Time ruler */}
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "15px",
                              background: "rgba(0,0,0,0.3)",
                              borderBottom: "1px solid #f0a500",
                            }}
                          >
                            {Array.from({
                              length: Math.ceil(duration) + 1,
                            }).map((_, i) => (
                              <div
                                key={i}
                                style={{
                                  position: "absolute",
                                  left: `${i * pixelsPerSecond}px`,
                                  top: 0,
                                  width: "1px",
                                  height: "6px",
                                  background: "#f0a500",
                                }}
                              >
                                <span
                                  style={{
                                    position: "absolute",
                                    left: "3px",
                                    top: "0",
                                    fontSize: "9px",
                                    color: "#f0a500",
                                  }}
                                >
                                  {i}s
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Previous position region (continues until prevPosEnd) */}
                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              top: "20px",
                              width: `${prevPosEnd * pixelsPerSecond}px`,
                              height: "15px",
                              background: "rgba(74, 144, 226, 0.6)",
                              borderRadius: "2px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "9px",
                              color: "#fff",
                            }}
                          >
                            Prev tracks
                          </div>

                          {/* Next position region (starts at nextPosStart) */}
                          <div
                            style={{
                              position: "absolute",
                              left: `${nextPosStart * pixelsPerSecond}px`,
                              top: "40px",
                              width: `${(duration - nextPosStart) * pixelsPerSecond}px`,
                              height: "15px",
                              background: "rgba(123, 220, 181, 0.6)",
                              borderRadius: "2px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "9px",
                              color: "#fff",
                            }}
                          >
                            Next tracks
                          </div>

                          {/* prevPosEnd marker */}
                          <div
                            style={{
                              position: "absolute",
                              left: `${prevPosEnd * pixelsPerSecond}px`,
                              top: "15px",
                              width: "2px",
                              height: "45px",
                              background: "#4a90e2",
                              opacity: 0.8,
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                top: "-12px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                fontSize: "8px",
                                color: "#4a90e2",
                                whiteSpace: "nowrap",
                              }}
                            >
                              prev ends
                            </span>
                          </div>

                          {/* nextPosStart marker */}
                          <div
                            style={{
                              position: "absolute",
                              left: `${nextPosStart * pixelsPerSecond}px`,
                              top: "15px",
                              width: "2px",
                              height: "45px",
                              background: "#7bdcb5",
                              opacity: 0.8,
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                bottom: "-12px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                fontSize: "8px",
                                color: "#7bdcb5",
                                whiteSpace: "nowrap",
                              }}
                            >
                              next starts
                            </span>
                          </div>

                          {/* Playhead */}
                          <div
                            style={{
                              position: "absolute",
                              left: `${transitionProgress * duration * pixelsPerSecond}px`,
                              top: 0,
                              width: "3px",
                              height: "100%",
                              background: "#ff4444",
                              boxShadow: "0 0 10px rgba(255, 68, 68, 0.9)",
                              zIndex: 10,
                            }}
                          />
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            <div
              className="parameter-controls"
              style={{
                margin: "20px 0",
                padding: "20px",
                background: "#0a0c12",
                borderRadius: "8px",
                border: "1px solid #0f1218",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "40px",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                {/* Parameter A - Vertical Slider */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#6a7080",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    Volume
                  </span>
                  <div
                    style={{
                      position: "relative",
                      width: "24px",
                      height: "80px",
                    }}
                  >
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={parameterA}
                      onChange={(e) =>
                        setParameterA(parseFloat(e.target.value))
                      }
                      className="vertical-slider"
                      style={{
                        position: "absolute",
                        width: "80px",
                        height: "6px",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%) rotate(-90deg)",
                        transformOrigin: "center",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#d9dada",
                      fontWeight: "500",
                    }}
                  >
                    {parameterA.toFixed(2)}
                  </span>
                </div>

                {/* Parameter B - Vertical Slider */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#6a7080",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    Filter
                  </span>
                  <div
                    style={{
                      position: "relative",
                      width: "24px",
                      height: "80px",
                    }}
                  >
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={parameterB}
                      onChange={(e) =>
                        setParameterB(parseFloat(e.target.value))
                      }
                      className="vertical-slider"
                      style={{
                        position: "absolute",
                        width: "80px",
                        height: "6px",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%) rotate(-90deg)",
                        transformOrigin: "center",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#d9dada",
                      fontWeight: "500",
                    }}
                  >
                    {parameterB.toFixed(2)}
                  </span>
                </div>

                {/* Transition Target Buttons */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#6a7080",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    Transition
                  </span>
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      height: "80px",
                      alignItems: "center",
                    }}
                  >
                    {[0, 1, 2].map((param) => (
                      <button
                        key={param}
                        onClick={() => setTransitionParam(param)}
                        style={{
                          width: "32px",
                          height: "32px",
                          background:
                            transitionParam === param ? "#4a9eb5" : "#1a1f28",
                          color:
                            transitionParam === param ? "#030509" : "#6a7080",
                          border:
                            transitionParam === param
                              ? "1px solid #4a9eb5"
                              : "1px solid #2a2f38",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: "600",
                          fontSize: "12px",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {param}
                      </button>
                    ))}
                  </div>
                  <span style={{ fontSize: "10px", color: "#a3a3a3" }}>
                    {transitionParam === 0
                      ? "None"
                      : `→ ${transitionParam === 1 ? "16" : "20"}s`}
                  </span>
                </div>
              </div>
            </div>

            <div className="player-controls">
              <button
                onClick={handleStart}
                disabled={!isLoaded || isPlaying}
                className="control-btn start-btn"
              >
                Start
              </button>
              <button
                onClick={handleStop}
                disabled={!isPlaying}
                className="control-btn stop-btn"
              >
                Stop
              </button>
              <button
                onClick={handleReset}
                disabled={!isLoaded}
                className="control-btn reset-btn"
              >
                Reset
              </button>
              <button
                onClick={handleTransition}
                disabled={!isPlaying || isTransitioning || pendingTransition}
                className={`control-btn transition-btn ${isTransitioning || pendingTransition ? "active" : ""}`}
              >
                Transition
              </button>
            </div>
          </>
        )}
      </div>
    </MusicStateContext.Provider>
  );
};

export default AdaptiveMusicPlayer;
